import { useEffect, FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toast } from 'sonner';
import { BaseModal, BaseSelect, ConfirmationModal, FormPlaceholder } from 'wbs-components-lib';

import { Box, Typography } from '@mui/material';

import { BetOutcomeResultTypesEnum } from '@shared/constants';

import { useFinishBetEventMutation, useGetBetOutcomesQuery } from '@services';

import { AppDispatch, getBetOutcomesByEventIdSelector, setBetOutcomes, updateBetEvent } from '@store';

import { BetEventFinishModalProps } from './types';
import {
  getBetOutcomeResultTypeSelectItems,
  getInitialBetOutcomesMapWithResults,
  getIsResultTypesApplied,
} from './utils';

import styles from './BetEventFinishModal.module.css';

const { modalHeader, modalItem, modalContainer } = styles;

export const BetEventFinishModal: FC<BetEventFinishModalProps> = ({ isOpened, handleClose, betEvent }) => {
  const dispatch = useDispatch<AppDispatch>();

  const { id: betEventId } = betEvent;

  const [isConfirmationModalOpened, setIsConfirmationModalOpened] = useState(false);

  const currentBetEventOutcomes = useSelector(getBetOutcomesByEventIdSelector(betEventId));
  const [finishBetEvent, { isLoading: isEventFinishing }] = useFinishBetEventMutation();
  const [localBetEventOutcomesMap, setLocalBetEventOutcomesMap] = useState<Record<string, BetOutcomeResultTypesEnum>>(
    {}
  );

  useEffect(() => {
    if (currentBetEventOutcomes.length !== 0) {
      const initialBetEventOutcomesMap = getInitialBetOutcomesMapWithResults(currentBetEventOutcomes);

      setLocalBetEventOutcomesMap(initialBetEventOutcomesMap);
    }
  }, [currentBetEventOutcomes]);

  const isBetEventOutcomesEmpty = currentBetEventOutcomes.length === 0;

  const { data: betEventOutcomes, isLoading: isOutcomesLoading } = useGetBetOutcomesQuery(betEventId, {
    refetchOnMountOrArgChange: true,
    skip: !isBetEventOutcomesEmpty || !isOpened,
  });

  useEffect(() => {
    if (betEventOutcomes !== undefined) {
      dispatch(setBetOutcomes({ betEventOutcomes, betEventId }));
    }
  }, [betEventOutcomes]);

  const resultTypeSelectItems = getBetOutcomeResultTypeSelectItems();

  const onFinishBetEvent = async () => {
    const { betEvent, betEventOutcomes } = await finishBetEvent({
      betEventId,
      payload: { results: localBetEventOutcomesMap },
    }).unwrap();

    dispatch(updateBetEvent(betEvent));

    dispatch(setBetOutcomes({ betEventId, betEventOutcomes }));

    toast.success('Success: Bet event has been finished!');

    handleConfirmationModalClose();

    handleClose();
  };

  const updateBetOutcomeResultType = (betOutcomeId: string, resultType: BetOutcomeResultTypesEnum) => {
    setLocalBetEventOutcomesMap((previousMap) => ({
      ...previousMap,
      [betOutcomeId]: resultType,
    }));
  };

  const handleConfirmationModalOpen = () => {
    setIsConfirmationModalOpened(true);
  };

  const handleConfirmationModalClose = () => {
    setIsConfirmationModalOpened(false);
  };

  const isResultTypesApplied = getIsResultTypesApplied(localBetEventOutcomesMap);
  const isSubmitButtonDisabled = isBetEventOutcomesEmpty ? true : !isResultTypesApplied;

  return (
    <>
      <BaseModal
        headerTitle="Finish Event"
        isOpened={isOpened}
        handleClose={handleClose}
        buttonConfirmedText="Finish"
        isSubmitButtonDisabled={isSubmitButtonDisabled}
        onSubmit={handleConfirmationModalOpen}
      >
        {!isOutcomesLoading ? (
          <>
            {!isBetEventOutcomesEmpty ? (
              <Box className={modalContainer}>
                <Box className={modalHeader}>
                  <Typography fontWeight="600">NAME</Typography>
                  <Typography fontWeight="600">TYPE</Typography>
                </Box>
                {currentBetEventOutcomes.map((betEventOutcome) => {
                  const { name, id: betOutcomeId } = betEventOutcome;

                  const resultType = localBetEventOutcomesMap[betOutcomeId] ?? BetOutcomeResultTypesEnum.PENDING;

                  return (
                    <Box className={modalItem}>
                      <Typography>{name}</Typography>
                      <BaseSelect
                        items={resultTypeSelectItems}
                        handleChange={(value: BetOutcomeResultTypesEnum) =>
                          updateBetOutcomeResultType(betOutcomeId, value)
                        }
                        value={resultType}
                        // @ts-ignore
                        placeholder="Select"
                        label="Select type"
                      />
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography margin="50px">No outcomes to finish</Typography>
            )}
          </>
        ) : (
          <FormPlaceholder />
        )}
      </BaseModal>
      <ConfirmationModal
        buttonConfirmedText="Finish"
        headerTitle="Finish Event"
        onSubmit={onFinishBetEvent}
        isOpened={isConfirmationModalOpened}
        isLoading={isEventFinishing}
        handleClose={handleConfirmationModalClose}
      >
        <Typography color="red">
          If you set "positive" result type for outcome therefore all prizes will be sent automatically to users which
          make a bet on this outcome.
        </Typography>
        <Typography>Are you sure that you want to finish this event?</Typography>
      </ConfirmationModal>
    </>
  );
};
